import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { 
  Shield, BrainCircuit, Network, Target, 
  Zap, Database, Terminal, 
  Truck, Store, Cpu, Activity, Lock, 
  Globe, Radar, Radio, Command, 
  Home, Cloud, Satellite, Microscope,
  Ship, Wheat, Landmark, Scale, GraduationCap,
  PlayCircle, Server, Info, ArrowUpRight,
  TrendingUp, Workflow, Maximize2, X,
  Plane, Train, Box, MapPin, Snowflake, Factory, 
  Boxes, AlertTriangle, Fingerprint, ZapOff, 
  TrendingDown, Layers, ChevronUp, Eye, EyeOff,
  Car, Briefcase, Pickaxe, HardHat, 
  Unplug, ShieldCheck, BoxSelect, Bot, UserCheck, ShieldAlert, Laptop,
  Settings, Key, BarChart3, RadioTower, ShoppingCart, Gem, Wrench, Scan, ArrowDown, Flame
} from 'lucide-react';

export const IndustryBrain = () => {
  const [activeTab, setActiveTab] = useState('index'); // 'index' or 'flow'
  const [hoveredSector, setHoveredSector] = useState(null);
  const [zoomedSectorData, setZoomedSectorData] = useState(null);
  const [traceActive, setTraceActive] = useState(false);
  const [heatmapActive, setHeatmapActive] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // --- LIVE UNIVERSE COUNTER ---
  const [universeCount, setUniverseCount] = useState(109440); // Base: 96 sectors * 30 segments * 38 entities

  useEffect(() => {
    // Simulate active system parsing and discovering new entities
    const interval = setInterval(() => {
      setUniverseCount(prev => prev + Math.floor(Math.random() * 2) + 1); // Increments by 1 or 2
    }, 4200); // Fires every ~4.2 seconds
    return () => clearInterval(interval);
  }, []);

  // --- SCREENSAVER INACTIVITY TIMER LOGIC ---
  const timeoutRef = useRef(null);
  const isUnlockedRef = useRef(isUnlocked);

  // Sync ref with state so event listeners don't need to be constantly re-bound
  useEffect(() => {
    isUnlockedRef.current = isUnlocked;
    if (isUnlocked) {
      resetTimer(); // Start the countdown immediately upon unlocking
    }
  }, [isUnlocked]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    // Only run the timer if the dashboard is currently unlocked
    if (isUnlockedRef.current) {
      timeoutRef.current = setTimeout(() => {
        setIsUnlocked(false);
        setIsTransitioning(false); // Ensure transition state is reset so it's visible when it returns
      }, 120000); // 120,000ms = exactly 2 minutes
    }
  }, []);

  // Global activity listeners
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keypress', 'wheel', 'touchstart', 'scroll'];
    const handleActivity = () => {
      if (isUnlockedRef.current) resetTimer();
    };

    events.forEach(e => window.addEventListener(e, handleActivity));
    
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetTimer]);

  const handleUnlock = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsUnlocked(true);
      setIsTransitioning(false); // Reset so it doesn't stay hidden when the screensaver pops back up
    }, 1200);
  };

  // --- LIVE INTERROGATION ENTITIES ---
  const tickerEntities = [
    { name: "Starlink", sector: "Space", status: "Physics Audit OK", icon: Satellite },
    { name: "Tempus AI", sector: "HCLS", status: "Thesis Authenticated", icon: Microscope },
    { name: "Flexport", sector: "Logistics", status: "Multiple Expansion Validated", icon: Truck },
    { name: "Anduril", sector: "Defense", status: "Thesis Authenticated", icon: Radar },
    { name: "CoreWeave", sector: "Compute", status: "Multiple Expansion Validated", icon: Server },
    { name: "Stripe", sector: "Fintech", status: "Physics Audit OK", icon: Landmark },
    { name: "Darktrace", sector: "Cyber", status: "Thesis Authenticated", icon: Lock },
    { name: "Adyen", sector: "Fintech", status: "Multiple Expansion Validated", icon: Activity },
    { name: "Planet", sector: "Space", status: "Physics Audit OK", icon: Eye },
    { name: "Palantir", sector: "IT Services", status: "Thesis Authenticated", icon: Terminal },
  ];

  const stackData = [
    {
      id: 'intent',
      title: 'Autonomous Intent',
      subtitle: 'The Commerce Layer',
      role: 'L4 • Outcome',
      color: 'purple',
      icon: Target,
      pivot: 'Outcome-Based Settlement (ARR)',
      sectors: [
        { 
          name: 'Retail', 
          active: true, 
          icon: Store, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'High', pivot: '12x', wrapper: false, 
          dependsOn: ['Logistics', 'Telecom', 'Compute'],
          deepDive: {
            themeColor: 'cyan',
            fullName: 'Retail & Commerce',
            scores: { moatDefensibility: '4.2 / 10', disruptionVelocity: '8.5 / 10', alphaLeverage: '9.0 / 10' },
            custodian: {
              name: 'Pedro Costa',
              role: 'Retail & Commerce',
              img: '/images/profile_pedro.jpg'
            },
            slices: [
              { name: 'Grocery, Pharmacy & FMCG', layer: 'Vertical', desc: 'Velocity & Unit Economics. Solving for perishable supply chains, high-frequency signal recovery, and micro-fulfillment.', focus: 'Q-Commerce, Perishables AI, Restaurant Tech (QSR), Pharmacy Tech', icon: ShoppingCart },
              { name: 'Fashion, Beauty & Luxury', layer: 'Vertical', desc: 'Brand Equity & Experience. Solving for high-fidelity spatial commerce, VIP clienteling, and authenticated provenance.', focus: 'Spatial Commerce, VIP Clienteling, Apparel Tech, Circularity (DPPs), Resale APIs', icon: Gem },
              { name: 'Home, DIY & Electronics', layer: 'Vertical', desc: 'Lifecycle & Service. Solving for long-cycle visibility, warranty/installation orchestration, and complex reverse logistics.', focus: 'Service Orchestration, Home-DIY Tech, Reverse Logistics, Lifecycle OS', icon: Wrench },
              { name: 'Marketplaces & Aggregators', layer: 'Operating DNA', desc: 'Network Yield & Orchestration. High-volume, asset-light platforms optimizing take-rates via ecosystem dominance and demand routing.', focus: 'Third-Party Ecosystems, Marketplace OS, Take-Rate Optimization, Multi-Vendor Settlements', icon: Network },
              { name: 'Big Box & Mass Retail', layer: 'Operating DNA', desc: 'Infrastructure Monetization. Transforming massive physical footprints into active fulfillment nodes and high-fidelity data sensors.', focus: 'Store-as-Fulfillment-Node, Fleet Orchestration, Labor-Efficiency AI, Asset Analytics', icon: Store },
              { name: 'Agentic & Social Commerce', layer: 'Interface Layer', desc: 'Search Death. The transition from \'Storefronts\' to \'Autonomous Discovery Agents\' that negotiate intent.', focus: 'Intent Engines, Discovery Bots, LLM Connectors, Conversational Settlement', icon: Bot },
              { name: 'Retail Media & Ad-Tech', layer: 'Intelligence Layer', desc: 'The 70% Margin Engine. Transforming transactional footprints into high-yield ad networks via signal recovery and identity.', focus: 'Ad-Tech (DSP/SSP), Identity Vaults (CDP), Predictive Merchandising, In-Store Media', icon: BrainCircuit },
              { name: 'Commerce Fintech & Payments', layer: 'Transaction Layer', desc: 'Decoupling the Monolith. Headless orchestration, embedded finance, and global payment agility.', focus: 'Composable Commerce, Programmable Payments, POS Modernization, Embedded Finance', icon: Server },
              { name: 'Supply Chain & Robotics', layer: 'Physical Layer', desc: 'The Software-Defined Moat. Hardening the supply chain through robotic nodes (WES), dark stores, and autonomy.', focus: 'Robotics (WES), Dark Store Architecture, Workforce Automation, Autonomous Mid-Mile', icon: Box }
            ]
          }
        },
        { name: 'Real Estate', active: false, icon: Home, sovereignty: 'S', debt: 'Med', pivot: '18x', wrapper: false, dependsOn: ['Energy & Utils', 'Cyber'] },
        { name: 'Hospitality', active: false, icon: Globe, sovereignty: 'W', debt: 'High', pivot: '9x', wrapper: true, dependsOn: ['Logistics', 'Cloud'] },
        { name: 'Media', active: false, icon: PlayCircle, sovereignty: 'S', debt: 'Low', pivot: '14x', wrapper: false, dependsOn: ['Telecom', 'Compute'] },
        { name: 'Ad-Tech', active: false, icon: Radar, sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, dependsOn: ['Cyber', 'Cloud', 'Telecom'] },
        { name: 'Mobility', active: false, icon: Car, sovereignty: 'S', debt: 'Med', pivot: '16x', wrapper: false, dependsOn: ['Logistics', 'Energy & Utils'] }
      ]
    },
    {
      id: 'cognitive',
      title: 'Cognitive Foundations',
      subtitle: 'The Intelligence Layer',
      role: 'L3 • Logic',
      color: 'cyan',
      icon: BrainCircuit,
      pivot: 'Labor Billing → Reasoning ARR',
      sectors: [
        { 
          name: 'HCLS', 
          active: true, 
          icon: Microscope, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'High', pivot: '18x', wrapper: false, 
          dependsOn: ['Cyber', 'Compute', 'Logistics'],
          deepDive: {
            themeColor: 'cyan',
            fullName: 'Healthcare & Life Sciences (HCLS)',
            scores: { moatDefensibility: '8.8 / 10', disruptionVelocity: '6.5 / 10', alphaLeverage: '9.5 / 10' },
            custodian: {
              name: 'Balbinder B.',
              role: 'HCLS & Bio-Digital',
              img: '/images/profile_balbinder.png'
            },
            council: [
              {
                name: 'Dr. Rajat Mani Thomas',
                role: 'Council Grounding',
                title: 'Applied A.I. Specialist | Asst. Prof @ Weill Cornell',
                img: '/images/profile_rajat.jpg'
              }
            ],
            slices: [
              { name: 'MedTech & Robotics', layer: 'Delivery Cluster', desc: 'The physical and virtual environments where care is administered, shifting from manual intervention to surgical autonomy.', focus: 'Surgical Autonomy, Robotic Assisted Surgery', icon: Bot },
              { name: 'Health Systems & Acute', layer: 'Delivery Cluster', desc: 'Hospital and acute care facilities leveraging ambient intelligence and capacity prediction.', focus: 'Ambient Intelligence, Capacity Prediction', icon: Activity },
              { name: 'Virtual Wards & Home Care', layer: 'Delivery Cluster', desc: 'Decentralized care delivery extending into the patient\'s home via remote monitoring and IoMT.', focus: 'Remote Monitoring, IoMT, Decentralized Care', icon: Home },
              { name: 'Biotech & Biopharma', layer: 'Innovation Cluster', desc: 'The R&D Engine where biological intelligence meets computational discovery.', focus: 'Generative Biology, Cell & Gene Therapy', icon: Microscope },
              { name: 'Diagnostics & Precision', layer: 'Innovation Cluster', desc: 'High-fidelity identification using multi-omic fusion and AI-driven radiology.', focus: 'Multi-omic Fusion, Biomarker AI, Radiology', icon: Target },
              { name: 'Contract Services (CRO/CDMO)', layer: 'Innovation Cluster', desc: 'Synthetic arms and AI-optimized manufacturing accelerating tech transfer.', focus: 'Synthetic Arms, AI Manufacturing, Tech Transfer', icon: Factory },
              { name: 'Life Sciences Tools', layer: 'Innovation Cluster', desc: 'The foundational technologies and autonomous lab infrastructure enabling advanced discovery.', focus: 'Gene Sequencing, Lab AI', icon: Cpu },
              { name: 'Payers & Managed Risk', layer: 'Finance & Ops Cluster', desc: 'The administrative and financial infrastructure orchestrating risk and ecosystem liquidity.', focus: 'Agentic Underwriting, Value-Based Risk', icon: ShieldCheck },
              { name: 'Pharmacy & PBMs', layer: 'Finance & Ops Cluster', desc: 'Managing the specialized supply chain and drug logistics ecosystems.', focus: 'Specialty Pharmacy, Drug Logistics', icon: Truck },
              { name: 'HealthTech Infrastructure', layer: 'Finance & Ops Cluster', desc: 'The operational rails enabling agentic workflow orchestration and secure data vaults.', focus: 'Agentic Workflow, Interoperability, Data Vaults', icon: Database }
            ]
          }
        },
        { 
          name: 'IT Services', 
          active: true, 
          icon: Terminal, 
          hasDeepDive: true,
          sovereignty: 'W', debt: 'Extreme', pivot: '14x', wrapper: true, 
          dependsOn: ['Cloud', 'Cyber', 'Telecom'],
          deepDive: {
            themeColor: 'teal',
            fullName: 'IT Services & Digital Eng.',
            scores: { moatDefensibility: '3.5 / 10', disruptionVelocity: '9.2 / 10', alphaLeverage: '9.8 / 10' },
            custodian: {
              name: 'Zuhaib',
              role: 'SDLC & IT Services',
              img: '/images/profile_zuhaib.jpg'
            },
            council: [
              {
                name: 'Balbinder B.',
                role: 'Council Grounding',
                title: 'HCLS & Defense Tech',
                img: '/images/profile_balbinder.png'
              },
              {
                name: 'Maheshwar D.',
                role: 'Council Grounding',
                title: 'Cyber & Sovereign Infra',
                img: '/images/profile_maheshwar.png'
              }
            ],
            slices: [
              { name: 'Global Systems Integration', layer: 'Legacy Transformation', desc: 'The Integration Spine. Complex orchestration of multi-generational technology stacks into a unified operating environment.', focus: 'Accenture, Deloitte, Infosys', icon: Layers },
              { name: 'AI-Native SDLC & DevOps', layer: 'The Software Factory', desc: 'The Engineering Artery. Shifting from manual syntax entry to agent-driven code generation and autonomous testing loops.', focus: 'GitHub, GitLab, Specialized AI Labs', icon: Terminal },
              { name: 'Managed Cloud & Infra', layer: 'Control Plane Ops', desc: 'The Logic Maintenance. Day-2 operations for hybrid and sovereign cloud estates. Managing the Environments of Record.', focus: 'Kyndryl, IBM, DXC Technology', icon: Cloud },
              { name: 'Agentic BPO & Automation', layer: 'The Execution Engine', desc: 'The Labor Pivot. Replacing high-volume manual processes with autonomous reasoning agents. Shifting from FTE to Outcome pricing.', focus: 'Genpact, WNS, Specialized AI BPOs', icon: Bot },
              { name: 'Data Engineering & LLMOps', layer: 'Intelligence Refinery', desc: 'The Source Prep. Forensic structuring and vectorization of proprietary data required to feed Enterprise RAG and models.', focus: 'Quantiphi, Tiger Analytics, LatentView', icon: Database },
              { name: 'Enterprise Platform Services', layer: 'Operating DNA', desc: 'The Core Orchestration. Modernizing enterprise Spines (SAP, Oracle, Salesforce) and their transition to agentic interfaces.', focus: 'TCS, HCLTech, Capgemini', icon: Server },
              { name: 'Product & Platform Eng.', layer: 'IP Creation', desc: 'The Venture Foundry. Building bespoke, proprietary digital assets and interfaces that allow clients to own their Industry Brain.', focus: 'EPAM, Globant, Thoughtworks', icon: BoxSelect },
              { name: 'Managed Cyber Ops (MSSP)', layer: 'Immune Operations', desc: 'The Defensive Pulse. Real-time operational arm monitoring, hunting, and neutralizing threats autonomously across the stack.', focus: 'Secureworks, Mandiant, Atos', icon: ShieldAlert },
              { name: 'GCC & Captive Advisory', layer: 'Sovereign Centers', desc: 'The Asset In-Sourcing. Strategic advisory for establishing Global Capability Centers to house a firm\'s core IP and AI talent.', focus: 'McKinsey, BCG, GCC Advisors', icon: Landmark }
            ]
          }
        },
        { 
          name: 'Fintech', 
          active: true, 
          icon: Landmark, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Med', pivot: '22x', wrapper: false, 
          dependsOn: ['Cyber', 'Compute', 'Telecom'],
          deepDive: {
            themeColor: 'amber',
            fullName: 'Fintech & Financial Infra',
            scores: { moatDefensibility: '6.5 / 10', disruptionVelocity: '8.0 / 10', alphaLeverage: '8.5 / 10' },
            custodian: {
              name: 'Balbinder B.',
              role: 'Financial Systems & Architecture',
              img: '/images/profile_balbinder.png'
            },
            council: [],
            slices: [
              { name: 'Digital Payments & Rails', layer: 'Payments', desc: 'Moving from card-swipes to real-time, bank-to-bank (A2A) and cross-border settlement.', focus: 'Stripe, Adyen, Wise, SWIFT', icon: Activity },
              { name: 'Modern Banking & Core Infra', layer: 'Banking', desc: 'Replacing legacy mainframes with cloud-native engines and national digital currency ledgers.', focus: 'Mambu, Thought Machine, CBDCs', icon: Landmark },
              { name: 'Lending & Credit Tech', layer: 'Lending', desc: 'Moving from FICO-based rules to real-time data streams and AI-driven risk assessment.', focus: 'Upstart, Affirm, SoFi', icon: Target },
              { name: 'Wealth & Investment Tech', layer: 'Wealth Management', desc: 'Hyper-personalized, automated portfolio management and institutional trading platforms.', focus: 'Addepar, Wealthfront, Aladdin', icon: TrendingUp },
              { name: 'InsurTech & Risk Transfer', layer: 'Insurance', desc: 'Automation of the full policy lifecycle—from parametric triggers to autonomous claims.', focus: 'Lemonade, Root, Descartes', icon: ShieldAlert },
              { name: 'Embedded Finance & BaaS', layer: 'B2B / SaaS', desc: 'Integrating financial tools directly into non-finance apps (Retail, Logistics, Workflows).', focus: 'Marqeta, Unit, Treasury Prime', icon: Layers },
              { name: 'Open Finance & Data APIs', layer: 'Interoperability', desc: 'The "Connective Tissue." Standardized APIs that allow data to flow between banks and agents.', focus: 'Plaid, Tink, Salt Edge', icon: Network },
              { name: 'Institutional Digital Assets', layer: 'Capital Markets', desc: 'Institutional-grade infrastructure for tokenized real-world assets (RWA) and stablecoins.', focus: 'Fireblocks, Circle, Institutional Defi', icon: Database },
              { name: 'RegTech & Financial Security', layer: 'Compliance', desc: 'Real-time, autonomous monitoring of fraud, AML, and state-level financial perimeters.', focus: 'Feedzai, Quantexa, Onfido', icon: ShieldCheck }
            ]
          }
        },
        { name: 'Legal', active: false, icon: Scale, sovereignty: 'W', debt: 'Extreme', pivot: '12x', wrapper: true, dependsOn: ['Cyber', 'Cloud'] },
        { name: 'GovTech', active: false, icon: Shield, sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, dependsOn: ['Defense', 'Cyber', 'Telecom'] },
        { name: 'EdTech', active: false, icon: GraduationCap, sovereignty: 'W', debt: 'High', pivot: '10x', wrapper: true, dependsOn: ['Cloud', 'Compute'] },
        { name: 'Prof Services', active: false, icon: Briefcase, sovereignty: 'W', debt: 'Extreme', pivot: '12x', wrapper: true, dependsOn: ['Cloud', 'Cyber'] }
      ]
    },
    {
      id: 'infrastructure',
      title: 'Digital Infrastructure',
      subtitle: 'The Flow Layer',
      role: 'L2 • Network',
      color: 'indigo',
      icon: Network,
      pivot: 'Commodity Utility → Intel Routing',
      sectors: [
        { 
          name: 'Telecom', 
          active: true, 
          icon: Radio, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Low', pivot: '10x', wrapper: false, 
          dependsOn: ['Energy & Utils', 'Space', 'Semis'],
          deepDive: {
            themeColor: 'indigo',
            fullName: 'Telecom & Connectivity',
            scores: { moatDefensibility: '9.0 / 10', disruptionVelocity: '5.5 / 10', alphaLeverage: '7.5 / 10' },
            custodian: {
              name: 'Maheshwar D.',
              role: 'Cyber & Sovereign Infrastructure',
              img: '/images/profile_maheshwar.png'
            },
            council: [
              {
                name: 'Prof. Merouane Debbah',
                role: 'Council Grounding',
                title: 'Telco AI & 6G',
                img: '/images/profile_merouane.jpg'
              },
              {
                name: 'Samer Geissah',
                role: 'Council Grounding',
                title: 'Head of AI @ Nokia | Ex-TELUS, du, Siemens',
                img: '/images/profile_samer.jpg'
              }
            ],
            slices: [
              { name: 'Fiber & Subsea Foundations', layer: 'Passive Backbone', desc: 'The Global Artery. Ownership of the intercontinental maritime corridors.', focus: 'Lumen, Tata, FiberCos', icon: Network },
              { name: 'Tower & Site Infrastructure', layer: 'Passive Access', desc: 'The Site Interface. Ownership of macro-towers and neutral hosts.', focus: 'American Tower, Cellnex, TowerCos', icon: Radio },
              { name: 'Spectrum & National Frequency', layer: 'Sovereign Asset', desc: 'The Digital Real Estate. Management and optimization of finite radio bands.', focus: 'National Regulators, Spectrum Arbitrage', icon: Activity },
              { name: 'Virtualized Radio Systems', layer: 'Active Access', desc: 'The Pulse Control. Cloud-native radio networks (OpenRAN) powered by telco AI.', focus: 'Mavenir, Samsung, vRAN/OpenRAN', icon: Cpu },
              { name: 'Integrated Telco Cloud & Core', layer: 'Network Logic', desc: 'The Virtualized Core. Secure, localized cloud perimeters.', focus: 'Ericsson, Nokia, Sovereign Telco Cloud', icon: Cloud },
              { name: 'Orbital Connectivity (NTN)', layer: 'Resilience Layer', desc: 'The Cross-Stack Extender. Logic managing handovers between towers and satellites.', focus: 'Starlink, AST SpaceMobile', icon: Globe },
              { name: 'Programmable NaaS & APIs', layer: 'Extraction Layer', desc: 'The Network Operating System. Connectivity platforms allowing AI agents to buy bandwidth.', focus: 'Vonage, Infobip, NaaS/APIs', icon: Terminal }
            ]
          }
        },
        { 
          name: 'Cyber', 
          active: true, 
          icon: Lock, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Med', pivot: '15x', wrapper: false, 
          dependsOn: ['Defense', 'Compute'],
          deepDive: {
            themeColor: 'emerald',
            fullName: 'Cybersecurity',
            scores: { moatDefensibility: '7.5 / 10', disruptionVelocity: '9.5 / 10', alphaLeverage: '9.6 / 10' },
            custodian: {
              name: 'Maheshwar D.',
              role: 'Cyber & Tech Infrastructure',
              img: '/images/profile_maheshwar.png'
            },
            slices: [
              { name: 'Identity & Access Mgmt (IAM)', layer: 'The Nucleus', desc: 'The Identity Perimeter. Managing cryptographic vaults to verify users and bots.', focus: 'Okta, Ping, ForgeRock, Auth0', icon: UserCheck },
              { name: 'Network & Edge Security', layer: 'Flow Protection', desc: 'The Connectivity Perimeter. SASE/ZTA embedded into the network pulse.', focus: 'Palo Alto, Zscaler, Cloudflare', icon: ShieldCheck },
              { name: 'Endpoint & Workload Security', layer: 'Machine Perimeter', desc: 'The Device Perimeter. Securing hosts where reasoning agents execute.', focus: 'CrowdStrike, SentinelOne', icon: Laptop },
              { name: 'Adversarial AI & Defense', layer: 'The Immune System', desc: 'The Logic Perimeter. Using reasoning models to neutralize autonomous threat vectors.', focus: 'XDR, SOAR, Darktrace, AI Red-Teaming', icon: BrainCircuit },
              { name: 'Application & API Security', layer: 'Integrity Engine', desc: 'The Transaction Perimeter. Hardening the interface where agents communicate.', focus: 'F5, Akamai, Imperva, Checkmarx', icon: BoxSelect },
              { name: 'Data Security & Governance', layer: 'National Asset', desc: 'The Residency Perimeter. Forensic tools ensuring 100% data residency.', focus: 'Varonis, BigID, Sovereign Encryption', icon: Database },
              { name: 'Industrial (OT) & Critical Infra', layer: 'Bedrock Hardening', desc: 'The Physical Perimeter. Protecting software and heavy machinery.', focus: 'Dragos, Nozomi, Grid Resilience', icon: ShieldAlert }
            ]
          }
        },
        { 
          name: 'Compute', 
          active: true, 
          icon: Server, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Low', pivot: '18x', wrapper: false, 
          dependsOn: ['Energy & Utils', 'Semis'],
          deepDive: {
            themeColor: 'blue',
            fullName: 'Compute & AI Factories',
            scores: { moatDefensibility: '9.2 / 10', disruptionVelocity: '9.0 / 10', alphaLeverage: '9.9 / 10' },
            custodian: {
              name: 'Maheshwar D.',
              role: 'Cyber & Tech Infrastructure',
              img: '/images/profile_maheshwar.png'
            },
            slices: [
              { name: 'AI Factories (HPC Data Centers)', layer: 'Specialized Infra', desc: 'Purpose-built for extreme power density and liquid cooling.', focus: 'CoreWeave, Lambda, Specialized PE Funds', icon: Server },
              { name: 'Hyperscale Infrastructure', layer: 'Wholesale Foundations', desc: 'Large-scale multi-tenant colocation and cloud on-ramps.', focus: 'Digital Realty, Equinix, Vantage', icon: Database },
              { name: 'Power & Thermal Engineering', layer: 'Mission-Critical Ops', desc: 'Specialized systems: liquid cooling (DLC) and high-fidelity UPS.', focus: 'Vertiv, Schneider, Eaton', icon: Snowflake },
              { name: 'Grid Interconnects & Storage', layer: 'The Energy Nexus', desc: 'Ownership of front-of-meter grid capacity. The Energy Land Bank.', focus: 'NextEra, Prologis Energy, Microgrid Ops', icon: Zap },
              { name: 'Managed GPU-as-a-Service', layer: 'Processing Engine', desc: 'Managed bare-metal inference for autonomous agents.', focus: 'GPU Clouds, MaaS', icon: Cpu },
              { name: 'Sovereign AI Clusters', layer: 'National Asset', desc: 'Nationalized compute capacity for state-level data residency.', focus: 'G42 (Condor Galaxy), Gov-Cloud', icon: ShieldCheck },
              { name: 'Distributed Edge Compute', layer: 'Latency Foundations', desc: 'Placing micro-compute within 10ms of end-nodes for real-time robotics.', focus: 'Akamai, Cloudflare, Ruggedized Edge', icon: Network }
            ]
          }
        },
        { 
          name: 'Cloud', 
          active: true, 
          icon: Cloud, 
          hasDeepDive: true,
          sovereignty: 'W', debt: 'Low', pivot: '12x', wrapper: true, 
          dependsOn: ['Compute', 'Telecom'],
          deepDive: {
            themeColor: 'sky',
            fullName: 'Cloud & Sovereign Orchestration',
            scores: { moatDefensibility: '8.5 / 10', disruptionVelocity: '8.5 / 10', alphaLeverage: '9.4 / 10' },
            custodian: {
              name: 'Maheshwar D.',
              role: 'Cyber & Tech Infrastructure',
              img: '/images/profile_maheshwar.png'
            },
            slices: [
              { name: 'Hyperscale Platforms (IaaS/PaaS)', layer: 'Global Foundations', desc: 'The Logic Utility. Global-scale pools of virtualized compute.', focus: 'AWS, Azure, GCP, Alibaba Cloud', icon: Cloud },
              { name: 'Sovereign Cloud & Residency', layer: 'National Asset', desc: 'Localized platforms designed for 100% data residency.', focus: 'Oracle Sovereign, G42 Cloud, GovClouds', icon: Globe },
              { name: 'Data Management & Lakehouses', layer: 'Persistence Layer', desc: 'The Logic Warehouse. Specialized environments for structured data persistence.', focus: 'Snowflake, Databricks, MongoDB', icon: Database },
              { name: 'Cloud Orchestration & FinOps', layer: 'Efficiency Layer', desc: 'The Margin Engine. Software managing multi-cloud complexity.', focus: 'HashiCorp, Datadog, Teradata', icon: Settings },
              { name: 'Hybrid & Private Cloud', layer: 'Bridge Infrastructure', desc: 'On-premise cloud architectures connecting private DCs to hyperscalers.', focus: 'VMware, Nutanix, IBM', icon: Layers },
              { name: 'AI Orchestration & RAG Hosting', layer: 'Intelligence Home', desc: 'Specialized environments for hosting LLMs and Private Knowledge Bases.', focus: 'Pinecone, Weights & Biases, LLM Ops', icon: BrainCircuit },
              { name: 'Vertical Cloud & Industry PaaS', layer: 'Specialized Logic', desc: 'Purpose-built stacks for specific industries with pre-certified compliance.', focus: 'Veeva, nCino, Specialized GovPaaS', icon: Briefcase }
            ]
          }
        },
        { 
          name: 'Space', 
          active: true, 
          icon: Satellite, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Low', pivot: '30x', wrapper: false, 
          dependsOn: ['Defense', 'Semis'],
          deepDive: {
            themeColor: 'violet',
            fullName: 'Space & Orbital Resilience',
            scores: { moatDefensibility: '9.8 / 10', disruptionVelocity: '4.5 / 10', alphaLeverage: '6.5 / 10' },
            custodian: {
              name: 'Maheshwar D.',
              role: 'Cyber & Sovereign Infrastructure',
              img: '/images/profile_maheshwar.png'
            },
            slices: [
              { name: 'Heavy Launch & Access', layer: 'Access Foundation', desc: 'The Artery. The heavy infrastructure required to move assets to orbit.', focus: 'SpaceX, Blue Origin, Arianespace', icon: Plane },
              { name: 'Satellite Systems (Upstream)', layer: 'Hardware Bedrock', desc: 'The Orbital Server. Manufacturing of standardized bus architectures.', focus: 'Lockheed, Airbus, Maxar', icon: Box },
              { name: 'Ground Segment & Teleports', layer: 'Terrestrial Interface', desc: 'The Earth On-Ramp. Physical antennae bridging signals to fiber.', focus: 'KSAT, SES, Azure Orbital', icon: Radio },
              { name: 'LEO/MEO Connectivity (NTN)', layer: 'The Pulse (Flow)', desc: 'The Orbital Extender. High-speed, low-latency broadband.', focus: 'Starlink, Kuiper, OneWeb', icon: Satellite },
              { name: 'PNT & Sovereign Timing', layer: 'The Sovereign Clock', desc: 'The Foundation of Truth. Constellations for global time-sync.', focus: 'GPS, Galileo, BeiDou, Private PNT', icon: Activity },
              { name: 'EO & Geospatial Intel', layer: 'Intelligence Engine', desc: 'The Global Sensor. Monitoring physical assets via high-fidelity sensing.', focus: 'Planet, BlackSky, ICEYE', icon: Eye },
              { name: 'Orbital Slot & Spectrum Mgmt', layer: 'Space Real Estate', desc: 'The Physics Moat. Managing finite orbital "slots" and frequencies.', focus: 'National Agencies, Slot Arbitrage', icon: MapPin },
              { name: 'SSA & Orbital Security', layer: 'Immune System', desc: 'Space Situational Awareness (SSA) and defense protecting assets.', focus: 'LeoLabs, Slingshot, Space Defense', icon: Radar }
            ]
          }
        }
      ]
    },
    {
      id: 'resilience',
      title: 'Sovereign Resilience',
      subtitle: 'The Physical Spine',
      role: 'L1 • Asset',
      color: 'emerald',
      icon: Shield,
      pivot: 'Maintenance Cost → Origin Moat',
      sectors: [
        { 
          name: 'Defense', 
          active: true, 
          icon: Radar, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Med', pivot: '12x', wrapper: false, 
          dependsOn: ['Mfg', 'Space', 'Cyber'],
          deepDive: {
            themeColor: 'cyan',
            fullName: 'Defense Tech 2.0',
            scores: { moatDefensibility: '9.5 / 10', disruptionVelocity: '7.0 / 10', alphaLeverage: '8.8 / 10' },
            custodian: {
              name: 'Balbinder B.',
              role: 'Defense & TMT',
              img: '/images/profile_balbinder.png'
            },
            council: [
              {
                name: 'Dr. Martin Crilly',
                role: 'Council Grounding',
                title: 'Former CTO (BAE) | CAIO @ Emirates',
                img: '/images/profile_martin.jpg'
              },
              {
                name: 'Frank R. Carus',
                role: 'Council Grounding',
                title: 'Ex-VP F-35 Program @ Northrop Grumman',
                img: '/images/profile_frank.jpg'
              }
            ],
            slices: [
              { name: 'Agentic AI & Autonomy', layer: 'L5 | Mission Intel', desc: 'Autonomous decision engines, mission-management models, and GPS-independent navigation logic.', focus: 'Decision Engines, Mission Models, GPS-Denied Nav', icon: BrainCircuit },
              { name: 'Precision Targeting & CV', layer: 'L5 | Mission Intel', desc: 'Multi-spectral sensor fusion, real-time ATR (Automatic Target Recognition), and compressed sensor-to-shooter engagement cycles.', focus: 'Sensor Fusion, Real-time ATR, Sensor-to-Shooter AI', icon: Target },
              { name: 'Battlefield OS & Interop', layer: 'L4 | Interop Fabric', desc: "Hardware-agnostic data middleware, cross-domain command systems (JADC2), and the software 'missing middle'.", focus: 'Middleware, JADC2, Cross-Domain Command', icon: Network },
              { name: 'Cyber & Electronic Warfare', layer: 'L3 | Transport Mesh', desc: 'Spectrum dominance platforms, advanced SIGINT/ELINT, offensive/defensive cyber operations, and RF-shielded hardware.', focus: 'Spectrum Dominance, SIGINT/ELINT, Offensive Cyber', icon: ShieldAlert },
              { name: 'Space & Orbital Infrastructure', layer: 'L3 | Transport Mesh', desc: 'Proliferated LEO (p-LEO) constellations, resilient SATCOM, Space Domain Awareness (SDA), and orbital logistics IP.', focus: 'p-LEO Constellations, Resilient SATCOM, SDA', icon: Globe },
              { name: 'Edge Compute & Energetics', layer: 'L2 | Compute Base', desc: 'Tactical AI accelerators, rad-hardened processing nodes, and ruggedized cloud-native edge infrastructure.', focus: 'Tactical AI, Rad-Hardened Nodes, Ruggedized Edge', icon: Cpu },
              { name: 'Attritable Mass & Robotics', layer: 'L1 | Physical Edge', desc: 'Unmanned multi-domain platforms (UAS/UGV/UUV), software-defined production lines, and low-cost expendable mass.', focus: 'UAS/UGV/UUV, Software-Defined Production, Expendable Mass', icon: Bot },
              { name: 'Digital Engineering & Sim', layer: 'Core | Foundation', desc: "Synthetic Training Environments (STE), high-fidelity digital twins, and accelerated 'sim-to-real' AI mission testing.", focus: 'STE, Digital Twins, Sim-to-Real AI', icon: Layers }
            ]
          }
        },
        { 
          name: 'Logistics', 
          active: true, 
          icon: Truck,
          hasDeepDive: true,
          sovereignty: 'S', debt: 'Extreme', pivot: '18x', wrapper: false,
          dependsOn: [],
          deepDive: {
            themeColor: 'cyan',
            fullName: 'Freight & Logistics',
            scores: { moatDefensibility: '7.0 / 10', disruptionVelocity: '7.5 / 10', alphaLeverage: '8.2 / 10' },
            custodian: {
              name: 'Thomas Kipp',
              role: 'Logistics AI & Global Supply',
              img: '/images/profile_Thomas.jpg'
            },
            slices: [
              { name: 'Maritime & Port Infrastructure', layer: 'Asset-Heavy', desc: 'The Global Arteries. Managing massive-scale containerization.', focus: 'Container Lines, Dry Bulk, Automated Terminals', icon: Ship },
              { name: 'Air Freight & Integrators', layer: 'Asset-Heavy', desc: "The Velocity Layer. High-value cargo and the global integrators.", focus: 'Air Cargo, Express Integrators', icon: Plane },
              { name: 'Surface Transport (TL & LTL)', layer: 'Asset-Right', desc: 'The Economic Pulse. Domestic trade backbone.', focus: 'Dry Van, Refrigerated, LTL Networks', icon: Truck },
              { name: 'Rail & Intermodal', layer: 'Asset-Heavy', desc: 'The High-Efficiency Spine. Lowest carbon footprint moving massive volumes.', focus: 'Class I Railroads, Intermodal Ramps', icon: Train },
              { name: 'Freight Forwarding', layer: 'Asset-Light', desc: 'The Trade Architects. Intellectual layer buying wholesale capacity.', focus: 'Ocean/Air Forwarding, Customs Brokerage', icon: Globe },
              { name: 'Contract Logistics & 3PL/4PL', layer: 'Asset-Light', desc: 'The Outsourced Brain. Partners managing warehousing and strategy.', focus: 'WMS SaaS, Fulfillment, Lead Log Providers', icon: Box },
              { name: 'Last-Mile & E-commerce', layer: 'Asset-Right', desc: 'The Friction Point. Most expensive leg solving for urban density.', focus: 'Parcel Delivery, Urban Hubs, Drones', icon: MapPin },
              { name: 'Specialized & Cold Chain', layer: 'Functional Moat', desc: 'High-Fidelity Chains. Solving for extreme constraints.', focus: 'Pharma Cold Chain, Heavy-Lift', icon: Snowflake },
              { name: 'Digital Freight & LogTech', layer: 'Systemic Layer', desc: 'The Operating System. Visibility and data layer.', focus: 'Digital Brokerage, TMS SaaS, AI Dispatch', icon: Cpu }
            ]
          }
        },
        { name: 'Energy & Utils', active: false, icon: Zap, sovereignty: 'S', debt: 'Extreme', pivot: '15x', wrapper: false, dependsOn: [] },
        { 
          name: 'Mfg', 
          active: true, 
          icon: Factory, 
          hasDeepDive: true,
          sovereignty: 'S', debt: 'High', pivot: '12x', wrapper: false, 
          dependsOn: ['Semis', 'Energy & Utils'],
          deepDive: {
            themeColor: 'orange',
            fullName: 'Advanced Manufacturing & Industrial OS',
            scores: { moatDefensibility: '8.2 / 10', disruptionVelocity: '6.8 / 10', alphaLeverage: '8.5 / 10' },
            custodian: {
              name: 'Thomas Kipp',
              role: 'Industrial Tech & Supply Chain',
              img: '/images/profile_Thomas.jpg'
            },
            slices: [
              { name: 'Industrial OS & Control', layer: 'Control Plane', desc: 'The real-time nervous system. Low-latency logic managing physical hardware and distributed machine intelligence.', focus: 'SCADA/HMI, DCS, Virtualized PLC, IIoT Connectivity', icon: Server },
              { name: 'Digital Eng. & Physics', layer: 'Design Layer', desc: 'The "Source Code" of physical assets. Environments where the asset is underwritten before it is built.', focus: 'CAD/CAE/EDA, PLM/PDM, Requirements, High-Fidelity Sim', icon: Layers },
              { name: 'Integrity & Compliance', layer: 'Integrity Layer', desc: 'The Sovereign Moat. Forensic tracking of quality, health, safety, and material provenance across the lifecycle.', focus: 'QMS, EHS, Risk Mitigation, Forensic Traceability', icon: ShieldCheck },
              { name: 'Mfg Execution & MOM', layer: 'Orchestration Layer', desc: 'The factory brain. Orchestrating the flow of labor, materials, and machine time to maximize yield.', focus: 'MES/MOM, Connected Worker, Dynamic Scheduling, CAM', icon: Network },
              { name: 'Asset Intel & Service', layer: 'Lifecycle Engine', desc: 'Moving from "Break-Fix" to "Anticipatory Maintenance" using vibration, acoustic, and thermal telemetry.', focus: 'EAM/CMMS, Predictive Maintenance (APM), Remote Service', icon: Activity },
              { name: 'Autonomous Execution', layer: 'Physical Arm', desc: 'Shifting from fixed automation to agentic robotics that adapt to production shifts in real-time.', focus: 'Collaborative Robotics (Cobots), Swarm AMRs, Additive Mfg', icon: Bot }
            ]
          }
        },
        { name: 'Semis', active: false, icon: Cpu, sovereignty: 'S', debt: 'Low', pivot: '25x', wrapper: false, dependsOn: ['Resources'] },
        { name: 'AgTech', active: false, icon: Wheat, sovereignty: 'S', debt: 'Med', pivot: '10x', wrapper: false, dependsOn: [] },
        { name: 'Resources', active: false, icon: Pickaxe, sovereignty: 'S', debt: 'Med', pivot: '8x', wrapper: false, dependsOn: [] },
        { name: 'Construction', active: false, icon: HardHat, sovereignty: 'S', debt: 'Extreme', pivot: '12x', wrapper: false, dependsOn: ['Mfg', 'Logistics'] }
      ]
    }
  ];

  const flowLayerSectors = [
    { name: 'Telecom', role: 'The Pulse (Transport)', desc: 'Nervous system of the economy. Moves Bits from Point A to Point B.', boundary: 'Owns throughput physics.', icon: Radio, color: 'indigo' },
    { name: 'Compute', role: 'The Engine (Processing)', desc: 'AI Factories converting electricity into reasoning capacity.', boundary: 'Owns power-to-intel physics.', icon: Cpu, color: 'blue' },
    { name: 'Cloud', role: 'The Warehouse (Logic)', desc: 'The virtualization layer. Home for data residency.', boundary: 'Owns accessibility physics.', icon: Cloud, color: 'cyan' },
    { name: 'Cyber', role: 'The Perimeter (Security)', desc: 'The immune system. Defines ZTA boundaries.', boundary: 'Owns verification physics.', icon: ShieldCheck, color: 'emerald' },
    { name: 'Space', role: 'The Extender (Reach)', desc: 'The orbital layer. Bridges terrestrial gaps.', boundary: 'Owns orbital resilience.', icon: Satellite, color: 'violet' }
  ];

  // --- DISRUPTION HEATMAP LOGIC ---
  const disruptionLevels = {
    'Retail': 'high', 'HCLS': 'high', 'IT Services': 'high', 'Cyber': 'high', 'Telecom': 'high', 'Media': 'high', 'Prof Services': 'high', 'Legal': 'high', 'EdTech': 'high',
    'Fintech': 'moderate', 'Logistics': 'moderate', 'Mfg': 'moderate', 'Cloud': 'moderate', 'Ad-Tech': 'moderate', 'Mobility': 'moderate', 'Hospitality': 'moderate',
    'Compute': 'low', 'Space': 'low', 'Defense': 'low', 'Energy & Utils': 'low', 'Semis': 'low', 'Resources': 'low', 'Construction': 'low', 'AgTech': 'low', 'Real Estate': 'low', 'GovTech': 'low'
  };

  const heatmapColors = {
    high: 'rose',
    moderate: 'indigo',
    low: 'emerald'
  };

  const stats = useMemo(() => {
    // Institutional Scale Metrics based on full taxonomy
    const industries = 26;
    const sectors = 96;
    const segments = sectors * 30; // 2,880

    return { industries, sectors, segments: segments.toLocaleString() };
  }, []);

  const getSystemicReach = () => {
    if (!hoveredSector) return [];
    const [layerId, sIdx] = hoveredSector.split('-');
    const layer = stackData.find(l => l.id === layerId);
    if (!layer) return [];
    const hoveredSectorName = layer.sectors[sIdx].name;

    const reach = [];
    stackData.forEach(l => {
      l.sectors.forEach(s => {
        if (s.dependsOn && s.dependsOn.includes(hoveredSectorName)) {
          reach.push(s.name);
        }
      });
    });

    return reach;
  };

  const activeReach = traceActive ? getSystemicReach() : [];
  const isHoveringWithReach = traceActive && hoveredSector && activeReach.length > 0;
  const currentHoveredName = hoveredSector ? stackData.find(l => l.id === hoveredSector.split('-')[0]).sectors[hoveredSector.split('-')[1]].name : '';

  const usesStackedView = (name) => name.includes('HCLS');

  const getClusterLayerLabel = (clusterName, sectorName) => {
    if (sectorName.includes('HCLS')) {
      if (clusterName === 'Delivery Cluster') return 'L3 • Value Realization';
      if (clusterName === 'Innovation Cluster') return 'L2 • IP Core';
      if (clusterName === 'Finance & Ops Cluster') return 'L1 • Systemic Rails';
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans flex flex-col items-center overflow-x-hidden relative">
      
      {/* --- MINIMALIST NEURAL LATTICE COVER --- */}
      {!isUnlocked && (
        <div 
          onClick={handleUnlock}
          className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#020617] transition-all duration-[1200ms] cursor-pointer overflow-hidden ${isTransitioning ? 'opacity-0 scale-[2] pointer-events-none' : 'opacity-100 scale-100'}`}
        >
          {/* BACKGROUND AMBIENCE */}
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(#334155_1.5px,transparent_1.5px)] [background-size:48px_48px]"></div>
          
          {/* FRONTIER BRANDING (MULTI-GRADIENT) */}
          <div className="absolute top-16 md:top-24 left-0 w-full flex flex-col items-center justify-center z-20">
             <div className="text-3xl md:text-5xl font-black uppercase tracking-[0.4em] pl-[0.4em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-400 to-purple-500">
               FRONTIER
             </div>
             <div className="mt-3 flex items-center gap-3 opacity-70">
               <div className="w-8 md:w-12 h-[1px] bg-gradient-to-r from-transparent to-slate-600"></div>
               <div className="text-[8px] md:text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400">Intelligence Perimeter</div>
               <div className="w-8 md:w-12 h-[1px] bg-gradient-to-l from-transparent to-slate-600"></div>
             </div>
          </div>

          {/* THE NEURAL LATTICE ANIMATION */}
          <div className="relative w-full h-full flex items-center justify-center">
             {/* CENTRAL CORE PULSE */}
             <div className="absolute w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px] animate-pulse"></div>
             
             {/* THE GEOMETRIC BRAIN / KNOWLEDGE BASE */}
             <div className="relative w-80 h-80 md:w-[400px] md:h-[400px] flex items-center justify-center">
                
                {/* --- TRUE 3D NEURAL CUBE (RUBIK'S MATRIX) --- */}
                <div className="cube-container absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  <div className="cube">
                    {['front', 'back', 'left', 'right', 'top', 'bottom'].map((face, fIdx) => (
                      <div key={face} className={`cube-face ${face}`}>
                         <div className="grid grid-cols-3 grid-rows-3 gap-1 md:gap-1.5 w-full h-full p-1 md:p-1.5">
                            {[...Array(9)].map((_, i) => (
                               <div 
                                 key={i} 
                                 className="cube-tile rounded-sm relative overflow-hidden"
                                 style={{ 
                                   animationDelay: `${(fIdx * 0.4 + i * 0.15)}s`,
                                   animationDuration: `${2.5 + (i % 3)}s` 
                                 }}
                               >
                                  {/* Tile Activity Indicators */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent"></div>
                                  {(i + fIdx) % 5 === 0 && <div className="absolute top-1.5 left-1.5 w-1 h-1 rounded-full bg-cyan-400/60 shadow-[0_0_5px_#22d3ee]"></div>}
                                  {(i + fIdx) % 7 === 0 && <div className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-sm bg-emerald-400/40"></div>}
                               </div>
                            ))}
                         </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* THE INTERNAL CORE (Abstract Dot Matrix) */}
                <div className="relative z-10 flex flex-col items-center justify-center transition-all duration-700 group">
                   <div className="w-32 h-32 md:w-36 md:h-36 rounded-full border border-slate-800/80 bg-slate-950/60 backdrop-blur-md shadow-[0_0_40px_rgba(0,0,0,0.8)] group-hover:border-indigo-500/50 group-hover:bg-indigo-500/10 flex items-center justify-center transition-all duration-500">
                      <svg width="64" height="64" viewBox="0 0 100 100" className="text-slate-400 group-hover:text-indigo-400 transition-colors duration-700 animate-[spin_80s_linear_infinite]">
                         {/* Center Core */}
                         <circle cx="50" cy="50" r="4" fill="currentColor" className="animate-pulse shadow-lg" />
                         
                         {/* Inner Ring */}
                         {[...Array(6)].map((_, i) => (
                            <circle key={`inner-${i}`} cx={50 + 16 * Math.cos(i * Math.PI / 3)} cy={50 + 16 * Math.sin(i * Math.PI / 3)} r="2.5" fill="currentColor" className="opacity-80" />
                         ))}
                         
                         {/* Middle Ring */}
                         {[...Array(12)].map((_, i) => (
                            <circle key={`mid-${i}`} cx={50 + 32 * Math.cos(i * Math.PI / 6)} cy={50 + 32 * Math.sin(i * Math.PI / 6)} r="1.5" fill="currentColor" className="opacity-50" />
                         ))}
                         
                         {/* Outer Asymmetric Nodes */}
                         {[...Array(8)].map((_, i) => (
                            <circle key={`outer-${i}`} cx={50 + 44 * Math.cos(i * Math.PI / 4 + 0.2)} cy={50 + 44 * Math.sin(i * Math.PI / 4 + 0.2)} r="1" fill="currentColor" className="opacity-20 animate-[pulse_3s_ease-in-out_infinite]" style={{ animationDelay: `${i * 0.2}s`}} />
                         ))}
                      </svg>
                   </div>
                </div>
             </div>

             {/* VERTICAL SCAN LINE */}
             <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent animate-[scan_6s_linear_infinite]"></div>
             </div>
          </div>

          {/* SYSTEM INITIALIZATION INDICATOR */}
          <div className="absolute bottom-12 md:bottom-16 flex flex-col items-center gap-3 opacity-80 animate-pulse">
             <div className="w-px h-10 bg-gradient-to-b from-indigo-500 to-transparent"></div>
             <div className="flex items-center gap-2">
                <Scan size={12} className="text-indigo-400" />
                <div className="text-[9px] font-black uppercase tracking-[0.4em] text-indigo-300 drop-shadow-[0_0_10px_rgba(99,102,241,0.4)]">Initialize Access</div>
             </div>
          </div>
        </div>
      )}

      {/* --- MAIN VAULT CONTENT --- */}
      <div className={`w-full min-h-screen p-4 md:p-8 flex flex-col items-center pb-40 transition-all duration-[1800ms] ${!isUnlocked ? 'blur-3xl scale-90 opacity-0' : 'blur-0 scale-100 opacity-100'}`}>
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:24px_24px]"></div>

        <div className="max-w-7xl mx-auto w-full relative z-10 transition-all duration-500" style={{ filter: zoomedSectorData ? 'blur(10px)' : 'none' }}>
          
          {/* Institutional Header & Command Bar */}
          <div className="mb-4 flex flex-col gap-4">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full border border-slate-800 text-[8px] font-black text-slate-500 uppercase tracking-[0.2em] bg-slate-900/50 mb-4 shadow-sm">
                <Command size={10} className="text-indigo-400" /> Alpha Foundry :: Intelligence Perimeter
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-none mb-8">
                Sector Brains <span className="text-slate-600">::</span> <br className="hidden lg:block" /><span className="text-slate-500 italic">The Institutional Intelligence Map.</span>
              </h1>
              
              {/* --- RESTORED & ENHANCED COMMAND BAR --- */}
              <div className="flex flex-wrap items-center gap-y-4 gap-x-6 md:gap-x-8 py-3.5 px-6 md:px-8 rounded-[20px] bg-[#060918]/80 border border-slate-700/80 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.4)] w-fit mb-10">
                 <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-400">
                    <Database size={14} className="text-emerald-500" />
                    Industries: <span className="text-white ml-1 text-sm md:text-base">{stats.industries}</span>
                 </div>
                 <div className="hidden sm:block w-px h-4 bg-slate-700/80"></div>
                 <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-400">
                    <Layers size={14} className="text-indigo-400" />
                    Sectors: <span className="text-white ml-1 text-sm md:text-base">{stats.sectors}</span>
                 </div>
                 <div className="hidden sm:block w-px h-4 bg-slate-700/80"></div>
                 <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-400">
                    <BoxSelect size={14} className="text-purple-400" />
                    Underwritten Segments: <span className="text-white ml-1 text-sm md:text-base">{stats.segments}</span>
                 </div>
                 <div className="hidden lg:block w-px h-4 bg-slate-700/80"></div>
                 <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-widest text-cyan-400">
                    <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_#22d3ee] mr-1"></div>
                    Monitored Universe: <span className="text-white tabular-nums ml-1 text-sm md:text-base">{universeCount.toLocaleString()}</span>
                 </div>
              </div>
            </div>
          </div>

          {/* --- STACK CONTROLS --- */}
          <div className="flex flex-col md:flex-row items-end justify-between gap-4 w-full mb-6 z-20 relative">
             <div className="flex-1 w-full md:w-auto min-h-[46px] flex flex-col md:flex-row items-start md:items-end gap-3">
                {activeTab === 'index' && isHoveringWithReach && (
                  <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border border-indigo-500/40 bg-slate-900/90 backdrop-blur-xl shadow-[0_0_30px_rgba(79,70,229,0.2)] transition-all duration-500 h-full w-fit ${isHoveringWithReach ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}`}>
                    <RadioTower size={14} className="text-indigo-400 animate-pulse shrink-0" />
                    <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 hidden sm:inline whitespace-nowrap">Impact Active:</span>
                    <span className="text-[9px] md:text-[10px] font-bold text-slate-200 uppercase tracking-wider">
                       <span className="text-indigo-300">{currentHoveredName}</span> is foundational for <span className="text-white bg-indigo-600 px-1.5 py-0.5 rounded ml-1">{activeReach.length}</span> downstream industries.
                    </span>
                  </div>
                )}
                
                {activeTab === 'index' && heatmapActive && (
                  <div className="flex items-center gap-3 sm:gap-4 text-[9px] font-black uppercase tracking-widest bg-slate-900/90 border border-slate-800 rounded-xl px-4 sm:px-5 py-3 h-full w-fit shadow-xl backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-center gap-2 text-rose-400"><div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shadow-[0_0_8px_#f43f5e]"></div> High</div>
                    <div className="w-px h-3 bg-slate-700"></div>
                    <div className="flex items-center gap-2 text-indigo-400"><div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_#6366f1]"></div> Moderate</div>
                    <div className="w-px h-3 bg-slate-700 hidden sm:block"></div>
                    <div className="hidden sm:flex items-center gap-2 text-emerald-400"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]"></div> Low</div>
                  </div>
                )}
             </div>
             
             <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 shrink-0">
                {activeTab === 'index' && (
                  <>
                    <button
                      onClick={() => setHeatmapActive(!heatmapActive)}
                      title="Toggle AI Disruption"
                      className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border h-[46px] ${heatmapActive ? 'bg-rose-500/10 text-rose-400 border-rose-500/40 shadow-[0_0_15px_rgba(244,63,94,0.2)]' : 'bg-slate-900/80 border-slate-800 text-slate-500 hover:text-slate-300 shadow-sm hover:bg-slate-800'}`}
                    >
                      <Flame size={14} className={heatmapActive ? "animate-pulse" : ""} />
                      <span className="uppercase">AI Disruption</span>
                    </button>

                    <button
                      onClick={() => setTraceActive(!traceActive)}
                      title="Simulate Systemic Shock"
                      className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border h-[46px] ${traceActive ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/40 shadow-[0_0_15px_rgba(79,70,229,0.2)]' : 'bg-slate-900/80 border-slate-800 text-slate-500 hover:text-slate-300 shadow-sm hover:bg-slate-800'}`}
                    >
                      <RadioTower size={12} className={traceActive ? "animate-pulse" : ""} />
                      <span className="uppercase">Simulate Shock</span>
                    </button>
                  </>
                )}
                
                <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-1.5 flex flex-wrap sm:flex-nowrap items-center gap-1 shadow-sm sm:h-[46px]">
                  <button
                    onClick={() => setActiveTab('index')}
                    className={`px-4 sm:px-6 h-10 sm:h-full rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'index' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`}
                  >
                    The Index
                  </button>
                  <button
                    onClick={() => setActiveTab('flow')}
                    className={`px-4 sm:px-6 h-10 sm:h-full rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${activeTab === 'flow' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`}
                  >
                    Architecture
                  </button>
                </div>
             </div>
          </div>

          {/* --- TAB CONTENT: THE INDEX --- */}
          {activeTab === 'index' && (
            <div className="flex flex-col gap-6 md:gap-10 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {stackData.map((layer) => {
                const hasImpactInLayer = layer.sectors.some(s => activeReach.includes(s.name));
                const isHoveredLayer = hoveredSector && hoveredSector.startsWith(layer.id);
                
                return (
                  <div key={layer.id} className="group relative flex flex-col md:flex-row gap-2">
                    <div className="hidden md:flex flex-col items-center w-8 pt-4 relative">
                       <div className={`w-1 h-full rounded-full transition-all duration-700 ${hasImpactInLayer ? 'bg-indigo-500 shadow-[0_0_20px_#6366f1]' : isHoveredLayer ? `bg-${layer.color}-500 shadow-[0_0_15px_rgba(var(--${layer.color}-500-rgb),0.5)]` : `bg-gradient-to-b from-${layer.color}-500/30 to-transparent shadow-[0_0_10px_rgba(var(--${layer.color}-500-rgb),0.1)] group-hover:from-${layer.color}-500/60`}`}></div>
                    </div>
                    <div className={`flex-1 bg-[#050a18]/90 border rounded-[32px] p-3 flex flex-col md:flex-row items-center gap-4 transition-all duration-700 backdrop-blur-sm ${hasImpactInLayer ? 'border-indigo-500/30 bg-[#070b1e]' : 'border-slate-800/80 group-hover:border-slate-700/80 group-hover:bg-[#070e24] shadow-xl'}`}>
                      <div className="w-full md:w-56 shrink-0 flex items-center gap-4 border-b md:border-b-0 md:border-r border-slate-800/50 pb-3 md:pb-0 md:pr-4">
                         <div className={`p-2.5 rounded-xl border transition-colors duration-500 ${hasImpactInLayer ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-400' : `bg-slate-900 border-slate-800 text-${layer.color}-500 shadow-inner`}`}>
                            <layer.icon size={20} />
                         </div>
                         <div className="flex flex-col">
                            <div className={`text-[8px] font-black uppercase tracking-[0.2em] mb-0.5 transition-colors ${hasImpactInLayer ? 'text-indigo-500/80' : `text-${layer.color}-500/80`}`}>{layer.role}</div>
                            <h3 className="text-sm font-black text-white leading-tight tracking-tight uppercase">{layer.title}</h3>
                            <div className="text-[9px] font-medium text-slate-400 italic">{layer.subtitle}</div>
                         </div>
                      </div>
                      <div className="flex-1 w-full overflow-x-auto scrollbar-hide py-1">
                         <div className="flex items-center gap-3 min-w-max">
                            {layer.sectors.map((sector, sIdx) => {
                              const isHoveredBlock = hoveredSector === `${layer.id}-${sIdx}`;
                              const isImpactedBlock = activeReach.includes(sector.name);
                              const isDimmed = isHoveringWithReach && !isHoveredBlock && !isImpactedBlock;
                              
                              const dLevel = disruptionLevels[sector.name] || 'low';
                              const heatColor = heatmapColors[dLevel];
                              
                              let tileClasses = "w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-2xl border-2 transition-all duration-500 flex flex-col items-center justify-center p-2 relative group/sector ";
                              tileClasses += sector.hasDeepDive ? "cursor-pointer " : "cursor-default ";

                              if (heatmapActive) {
                                // Illuminate ALL sectors when Heatmap is active
                                tileClasses += `bg-${heatColor}-500/10 border-${heatColor}-500/40 text-white shadow-[0_0_15px_rgba(0,0,0,0.2)] shadow-${heatColor}-500/20 `;
                                if (isHoveredBlock && !sector.wrapper) tileClasses += `bg-${heatColor}-500/20 scale-105 z-20 border-${heatColor}-500/80 `;
                              } else {
                                // Standard view relies on active/inactive states
                                if (sector.active) {
                                  tileClasses += `bg-slate-900/40 border-slate-700/80 text-white shadow-lg `;
                                  if (isHoveredBlock && !sector.wrapper) tileClasses += `border-${layer.color}-500/80 bg-slate-800 scale-105 z-20 shadow-[0_0_30px_rgba(0,0,0,0.3)] shadow-${layer.color}-500/30 `;
                                  if (isImpactedBlock) tileClasses += `border-indigo-500/80 bg-indigo-500/10 scale-[1.02] z-10 shadow-[0_0_20px_rgba(79,70,229,0.3)] shadow-indigo-500/30 `;
                                } else {
                                  tileClasses += `bg-slate-900/20 border-slate-800/60 text-slate-300 `;
                                  if (isHoveredBlock && !sector.wrapper) tileClasses += `border-slate-600 bg-slate-800 scale-105 z-20 `;
                                }
                              }

                              if (isDimmed) tileClasses += 'opacity-20 grayscale blur-[1px] ';
                              else tileClasses += 'opacity-100 ';
                              
                              let iconClass = "mb-2 transition-colors duration-300 ";
                              if (heatmapActive) {
                                iconClass += `text-${heatColor}-400`;
                              } else {
                                iconClass += isImpactedBlock ? 'text-indigo-400' : sector.active ? `text-${layer.color}-400` : 'text-slate-500 group-hover/sector:text-slate-300';
                              }

                              let textClass = "text-[9px] md:text-[10px] font-black uppercase tracking-tight text-center leading-[1.1] px-1 ";
                              if (heatmapActive) {
                                textClass += 'text-white';
                              } else {
                                textClass += isImpactedBlock || sector.active ? 'text-white' : 'text-slate-400 group-hover/sector:text-white';
                              }

                              return (
                                <div 
                                  key={sIdx}
                                  onMouseEnter={() => setHoveredSector(`${layer.id}-${sIdx}`)}
                                  onMouseLeave={() => setHoveredSector(null)}
                                  onClick={() => sector.hasDeepDive && setZoomedSectorData({...sector.deepDive, icon: sector.icon})}
                                  className={tileClasses}
                                >
                                   {isImpactedBlock && !heatmapActive && <div className="absolute -top-2 -right-2 bg-indigo-600 text-white text-[7px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg z-30 animate-pulse">Downstream</div>}
                                   <sector.icon size={22} className={iconClass} />
                                   <div className={textClass}>{sector.name}</div>
                                   {!sector.active && !isImpactedBlock && <Lock size={8} className={`absolute bottom-1.5 right-1.5 ${heatmapActive ? `text-${heatColor}-500/50` : 'text-slate-700'}`} />}
                                </div>
                              );
                            })}
                         </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* --- TAB CONTENT: ARCHITECTURE --- */}
          {activeTab === 'flow' && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 animate-in fade-in zoom-in-95 duration-500">
              {flowLayerSectors.map((s, idx) => (
                <div key={idx} className="bg-[#050a18]/90 border border-slate-800 hover:border-indigo-500/50 rounded-[40px] p-8 flex flex-col transition-all group backdrop-blur-md">
                   <div className={`w-14 h-14 rounded-2xl bg-${s.color}-500/10 border border-${s.color}-500/20 flex items-center justify-center text-${s.color}-400 mb-8 group-hover:scale-110 transition-transform`}>
                      <s.icon size={32} />
                   </div>
                   <div className={`text-[10px] font-black uppercase tracking-[0.2em] text-${s.color}-500 mb-1`}>{s.role}</div>
                   <h4 className="text-2xl font-black text-white mb-4 tracking-tight uppercase">{s.name}</h4>
                   <p className="text-xs text-slate-400 leading-relaxed font-medium mb-8 flex-1">{s.desc}</p>
                   <div className="pt-6 border-t border-slate-800/50">
                      <div className="flex items-center gap-2 mb-3">
                         <BoxSelect size={12} className="text-slate-500" />
                         <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Physics Boundary</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-bold leading-relaxed italic">{s.boundary}</div>
                   </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* --- LIVE INTERROGATION TICKER (GHOST MODE) --- */}
        <div className="fixed bottom-0 left-0 w-full py-3 z-40 overflow-hidden select-none pointer-events-none bg-gradient-to-t from-[#020617] via-[#020617]/90 to-transparent">
          <div className="flex items-center gap-12 whitespace-nowrap animate-[marquee_60s_linear_infinite] opacity-70">
            {[...tickerEntities, ...tickerEntities, ...tickerEntities].map((entity, i) => (
              <div key={i} className="inline-flex items-center gap-3">
                <entity.icon size={12} className="text-slate-400" />
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">{entity.name}</span>
                  <span className="text-[9px] text-slate-600 mx-1">///</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{entity.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* DEEP DIVE MODAL */}
        {zoomedSectorData && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 lg:p-8">
            <div className="absolute inset-0 bg-[#020617]/80 backdrop-blur-sm" onClick={() => setZoomedSectorData(null)}></div>
            
            <div className={`relative w-[96vw] max-w-[1400px] h-[94vh] border border-slate-800/40 rounded-[32px] shadow-2xl flex flex-col animate-in fade-in zoom-in duration-300 bg-[#060918]/95 backdrop-blur-2xl overflow-hidden`}>
               
               {/* SHARED SLEEK HEADER */}
               <div className="relative z-10 px-6 py-5 border-b border-slate-800/40 flex items-center justify-between shrink-0 bg-[#050a18]/40">
                  <div className="flex items-center gap-4">
                     <div className={`p-2 bg-[#020617] border border-${zoomedSectorData.themeColor}-500/30 rounded-xl shadow-inner`}>
                        <zoomedSectorData.icon size={20} className={`text-${zoomedSectorData.themeColor}-400`} />
                     </div>
                     <div>
                        <div className={`text-[8px] font-black uppercase tracking-[0.2em] text-${zoomedSectorData.themeColor}-400 mb-0.5 flex items-center gap-1.5`}><div className={`w-1.5 h-1.5 rounded-full bg-${zoomedSectorData.themeColor}-400 animate-pulse`}></div>Live Asset Sync</div>
                        <h2 className="text-xl md:text-2xl font-black text-white tracking-tighter uppercase leading-none">{zoomedSectorData.fullName}</h2>
                     </div>
                  </div>
                  
                  <div className="flex flex-wrap justify-end items-center gap-4">
                     {zoomedSectorData.custodian && (
                       <div className="hidden md:flex items-center gap-3 bg-slate-900/40 border border-slate-800/60 rounded-full py-1.5 pl-1.5 pr-4 shadow-sm">
                          <img src={zoomedSectorData.custodian.img} alt={zoomedSectorData.custodian.name} className="w-9 h-9 rounded-full object-cover border border-slate-700 shrink-0" />
                          <div className="flex flex-col justify-center">
                             <div className={`text-[8px] text-emerald-400 uppercase font-black tracking-widest leading-none mb-1`}>Brain Custodian</div>
                             <div className="text-xs font-bold text-white leading-none mb-0.5">{zoomedSectorData.custodian.name}</div>
                             <div className="text-[8px] text-slate-400 font-bold tracking-wider leading-none">{zoomedSectorData.custodian.role}</div>
                          </div>
                       </div>
                     )}
                     {zoomedSectorData.council?.map((c, i) => (
                       <div key={i} className="hidden lg:flex items-center gap-3 bg-slate-900/40 border border-slate-800/60 rounded-full py-1.5 pl-1.5 pr-4 shadow-sm">
                          <img src={c.img} alt={c.name} className="w-9 h-9 rounded-full object-cover border border-slate-700 shrink-0" />
                          <div className="flex flex-col justify-center">
                             <div className={`text-[8px] text-[#c084fc] uppercase font-black tracking-widest leading-none mb-1`}>{c.role}</div>
                             <div className="text-xs font-bold text-white leading-none mb-0.5">{c.name}</div>
                             <div className={`text-[8px] text-slate-400 font-bold tracking-wider leading-none truncate max-w-[120px]`}>{c.title}</div>
                          </div>
                       </div>
                     ))}
                     <button onClick={() => setZoomedSectorData(null)} className="p-2 bg-slate-900/80 border border-slate-800 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all shadow-sm shrink-0"><X size={16} /></button>
                  </div>
               </div>

               {/* INDUSTRY SCORES PANEL */}
               {zoomedSectorData.scores && (
                 <div className="bg-[#020617]/60 border-b border-slate-800/40 px-6 py-5 shrink-0 flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide">
                    {Object.entries(zoomedSectorData.scores).map(([key, val], i) => (
                      <div key={i} className="flex-1 min-w-[120px] bg-slate-900/60 border border-slate-800 p-4 rounded-xl flex flex-col items-center justify-center shadow-inner">
                         <div className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 text-center">
                           {key.replace(/([A-Z])/g, ' $1').trim()}
                         </div>
                         <div className={`text-xl md:text-2xl font-black text-${zoomedSectorData.themeColor}-400 leading-none drop-shadow-[0_0_10px_currentColor]`}>
                           {val}
                         </div>
                      </div>
                    ))}
                 </div>
               )}

               {usesStackedView(zoomedSectorData.fullName) ? (
                  /* --- COMPACT HIGH-DENSITY VIEW (HCLS) WITH CARDS --- */
                  <div className="p-6 bg-[#020617]/40 flex-1 overflow-y-auto scrollbar-hide flex flex-col min-h-0 relative">
                     <div className="absolute left-[34px] lg:left-[42px] top-8 bottom-8 w-px bg-slate-800/50 hidden md:block"></div>
                     <div className="flex flex-col gap-6 relative z-10">
                        {[...new Set(zoomedSectorData.slices.map(s => s.layer))].map((clusterName, cIdx) => {
                           const slices = zoomedSectorData.slices.filter(s => s.layer === clusterName);
                           if (slices.length === 0) return null;
                           const layerLabel = getClusterLayerLabel(clusterName, zoomedSectorData.fullName);

                           return (
                              <div key={cIdx} className="flex flex-col bg-slate-900/20 border border-slate-800/40 rounded-2xl p-4 lg:p-5 relative overflow-hidden shrink-0 backdrop-blur-sm">
                                 <div className={`text-[10px] font-black uppercase tracking-widest text-${zoomedSectorData.themeColor}-500 mb-4 flex items-center justify-between border-b border-slate-800/30 pb-2`}>
                                    <div className="flex items-center gap-2">
                                       <div className={`w-1.5 h-1.5 rounded-full bg-${zoomedSectorData.themeColor}-500`}></div>
                                       {clusterName} <span className="text-slate-500 hidden md:inline-block font-medium tracking-normal ml-1">— {layerLabel}</span>
                                    </div>
                                    <span className="text-[8px] px-2 py-0.5 bg-slate-900/80 border border-slate-800 rounded-md text-slate-400">
                                       {slices.length} Nodes
                                    </span>
                                 </div>
                                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {slices.map((slice, i) => (
                                       <div key={i} className="bg-slate-900/20 border border-slate-800/40 rounded-2xl p-4 hover:border-slate-700/60 hover:bg-slate-900/60 transition-all group flex flex-col h-full shadow-md hover:shadow-xl">
                                          <div className="flex items-start gap-3 mb-3">
                                             <div className={`p-2 bg-slate-950 border border-slate-800 rounded-xl text-${zoomedSectorData.themeColor}-500 group-hover:scale-110 group-hover:text-${zoomedSectorData.themeColor}-400 group-hover:border-${zoomedSectorData.themeColor}-500/30 transition-all shrink-0`}>
                                                <slice.icon size={16} />
                                             </div>
                                             <h4 className="text-[13px] font-bold text-white leading-tight mt-0.5">{slice.name}</h4>
                                          </div>
                                          <p className="text-[11px] text-slate-400 leading-relaxed font-medium mb-3 flex-1">{slice.desc}</p>
                                          <div className="mt-auto flex items-start gap-1.5 pt-3 border-t border-slate-800/30">
                                             <Globe size={10} className={`text-${zoomedSectorData.themeColor}-500/70 shrink-0 mt-[1px]`} />
                                             <div className="text-[8px] font-black uppercase tracking-widest text-slate-500 leading-snug">
                                                Focus: <span className="text-slate-300">{slice.focus}</span>
                                             </div>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           );
                        })}
                     </div>
                  </div>
               ) : (
                  /* --- CARD DOSSIER VIEW (FLOW LAYER SECTORS) --- */
                  <div className="p-6 lg:p-8 bg-[#020617]/40 flex-1 overflow-y-auto scrollbar-hide">
                     <div className="flex items-center gap-3 mb-8">
                       <Cpu size={16} className={`text-${zoomedSectorData.themeColor}-400`} />
                       <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Sector Segmentation: Proprietary Underwriting</span>
                       <div className="flex-1 h-px bg-slate-800/50"></div>
                     </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {zoomedSectorData.slices.map((slice, i) => (
                           <div key={i} className="bg-slate-900/20 border border-slate-800/40 rounded-2xl p-6 hover:border-slate-700/60 hover:bg-slate-900/60 transition-all group flex flex-col h-full relative overflow-hidden backdrop-blur-sm shadow-md hover:shadow-xl">
                              <div className="flex items-center justify-between mb-5">
                                 <div className={`p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-${zoomedSectorData.themeColor}-500 group-hover:scale-110 group-hover:text-${zoomedSectorData.themeColor}-400 transition-transform`}>
                                    <slice.icon size={22} />
                                 </div>
                                 <span className="text-[8px] font-black uppercase tracking-widest text-slate-500 border border-slate-800/50 px-2.5 py-1 rounded-md flex-shrink-0 bg-slate-950/50">{slice.layer}</span>
                              </div>
                              
                              <h4 className="text-[15px] font-bold text-white leading-tight mb-2.5">{slice.name}</h4>
                              <p className="text-xs text-slate-400 leading-relaxed font-medium mb-5 flex-1">{slice.desc}</p>
                              
                              <div className="mt-auto flex items-start gap-2 pt-4 border-t border-slate-800/30">
                                 <Globe size={12} className={`text-${zoomedSectorData.themeColor}-500/80 shrink-0 mt-[1px]`} />
                                 <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 leading-snug">
                                    Focus: <span className="text-slate-300">{slice.focus}</span>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               )}
            </div>
          </div>
        )}
      </div>
      
      {/* --- ANIMATION KEYFRAMES --- */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scan {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(100vh); }
        }
        @keyframes rotateCube {
          0% { transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg); }
          100% { transform: rotateX(360deg) rotateY(360deg) rotateZ(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes tilePulse {
          0% { background: rgba(15, 23, 42, 0.3); border-color: rgba(99, 102, 241, 0.1); box-shadow: inset 0 0 0 rgba(99,102,241,0); }
          100% { background: rgba(99, 102, 241, 0.15); border-color: rgba(99, 102, 241, 0.4); box-shadow: inset 0 0 15px rgba(99,102,241,0.2); }
        }
        
        /* 3D Cube Container Responsiveness */
        .cube-container {
          perspective: 1200px;
          width: 220px;
          height: 220px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        @media (min-width: 768px) {
          .cube-container {
            width: 320px;
            height: 320px;
          }
        }
        
        .cube {
          position: absolute;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          animation: rotateCube 80s linear infinite;
        }
        
        .cube-face {
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cube-tile {
          background: rgba(15, 23, 42, 0.4);
          border: 1px solid rgba(99, 102, 241, 0.15);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          animation: tilePulse ease-in-out infinite alternate;
        }
        
        /* Mobile translateZ (110px) */
        .cube-face.front  { transform: translateZ(110px); }
        .cube-face.back   { transform: rotateY(180deg) translateZ(110px); }
        .cube-face.left   { transform: rotateY(-90deg) translateZ(110px); }
        .cube-face.right  { transform: rotateY(90deg) translateZ(110px); }
        .cube-face.top    { transform: rotateX(90deg) translateZ(110px); }
        .cube-face.bottom { transform: rotateX(-90deg) translateZ(110px); }

        /* Desktop translateZ (160px) */
        @media (min-width: 768px) {
          .cube-face.front  { transform: translateZ(160px); }
          .cube-face.back   { transform: rotateY(180deg) translateZ(160px); }
          .cube-face.left   { transform: rotateY(-90deg) translateZ(160px); }
          .cube-face.right  { transform: rotateY(90deg) translateZ(160px); }
          .cube-face.top    { transform: rotateX(90deg) translateZ(160px); }
          .cube-face.bottom { transform: rotateX(-90deg) translateZ(160px); }
        }
      `}</style>
    </div>
  );
};
